import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// ONE input recipe for the whole dashboard (design audit T12/B2). Before this,
// three recipes were in circulation: this component, the inspector/GBP family
// (`rounded-[var(--radius)] bg-card border-border` + a 2px brand outline) and
// the survey/help family (`rounded`/`rounded-lg` + the ring). They agreed on
// nothing except being roughly rectangular.
//
// `inputBase` is exported because <textarea> and <select> cannot be the
// <Input> component but MUST carry the identical border, radius, surface and
// focus ring — a shared string is the only way one recipe survives.
//
// Notes on the pieces:
//  - `rounded-md` = --radius-md = 12px = --radius (the token default).
//  - `border-input` resolves to --color-border, so it is the same hairline the
//    inspector family drew by hand.
//  - the focus treatment is the offset ring (`ring-2 ring-ring ring-offset-2`),
//    never `outline-2` and never a `/NN` alpha ring (both banned by the audit).
// The focus treatment on its own. Split out (T14) for the one editable
// surface that must NOT look like a text box but must focus like one:
// components/editable-text.tsx, the builder's in-canvas contentEditable.
// Never `outline-2`, never a `/NN` alpha ring — both banned by the audit.
export const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export const inputBase =
  'w-full rounded-md border border-input bg-surface-input px-3 text-sm text-foreground ' +
  'ring-offset-background transition-colors duration-[var(--duration-fast)] ' +
  'placeholder:text-text-tertiary ' +
  focusRing +
  ' disabled:cursor-not-allowed disabled:opacity-50';

export const inputVariants = cva(
  cn(inputBase, 'flex file:border-0 file:bg-transparent file:text-sm file:font-medium'),
  {
    variants: {
      // `sm` is the compact inspector/GBP density (h-9). `default` (h-10) is
      // the page-form density every existing <Input> call site already had.
      size: {
        default: 'h-10 py-2',
        sm: 'h-9 py-1.5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

// The native `size` attribute (a character count) is omitted so the cva
// variant name is free — no call site in this repo used the native one.
export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, ...props }, ref) => {
    return (
      <input
        type={type}
        ref={ref}
        className={cn(inputVariants({ size }), className)}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';
